import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import ReCAPTCHA from 'react-google-recaptcha';
import { Box, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import sha256 from 'crypto-js/sha256';
import CryptoJS from 'crypto-js';
import ApiHelper from 'utils/apiHelper';
import { logout } from 'components/../redux/actions/auth';
import { validatePassword } from 'utils/commonHelpers';
import config from 'config';

const ChangePasswordDialog = ({ onClose }) => {
    const dispatch = useDispatch();
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isCaptchaSuccess, setIsCaptchaSuccess] = useState(false);
    const [lengthLimits, setLengthLimits] = useState({});
    const [isContainUpper, setIsContainUpper] = useState(true);
    const [isContainLower, setIsContainLower] = useState(true);
    const [isContainNumber, setIsContainNumber] = useState(true);
    const [spclChar, setSpclChar] = useState('!@#$%^&*()_+-={}[]|\\:;<>.?/');

    const getSecurityParams = async () => {
        const headers = {
            'Content-Type': 'application/json'
        };

        try {
            const res = await ApiHelper.post(
                '/api/auth/security_settings/',
                {
                    uid: queryParameters.get('uid')
                },
                headers,
                {},
                false
            );

            if (res.data && res.data.passwordSettings) {
                setLengthLimits({
                    usernameMaxLength: res.data.passwordSettings.usernameMaxLength || 120,
                    passwordMaxLength: res.data.passwordSettings.passwordMaxLength || 120,
                    passwordMinLength: res.data.passwordSettings.passwordMinLength || 8
                });
                setIsContainLower(res.data.passwordSettings.pwdLwrReq);
                setIsContainUpper(res.data.passwordSettings.pwdCapReq);
                setIsContainNumber(res.data.passwordSettings.pwdNumReq);
                setSpclChar(res.data.passwordSettings.SpclChar);
                return true;
            } else {
                return false;
            }
        } catch (err) {
            toast.error('Failed to fetch security settings:', err);
            return false;
        }
    };

    const handleSubmit = async () => {
        const isSecurityValid = await getSecurityParams();

        if (!isSecurityValid) {
            return;
        }

        if (oldPassword === '') {
            toast.error('Please type the current password.');
            return true;
        } else {
            if (newPassword === '') {
                toast.error('New password is required.');
                return true;
            }
            const result = validatePassword(
                password,
                lengthLimits.passwordMinLength,
                lengthLimits.passwordMaxLength,
                isContainUpper,
                isContainLower,
                isContainNumber,
                spclChar
            );

            if (!result.isValid) {
                result.errors.forEach((error) => {
                    toast.warning(error);
                });
                return true;
            }
            if (newPassword !== confirmPassword) {
                toast.error(`Confirm password is incorrect`);
                return true;
            }
        }

        ApiHelper.post('/api/auth/change_password/', {
            old_password: sha256(oldPassword).toString(CryptoJS.enc.Hex),
            new_password: sha256(newPassword).toString(CryptoJS.enc.Hex)
        })
            .then(() => {
                toast.success('Successfully changed.');
                onClose();
                dispatch(logout());
            })
            .catch((err) => {
                if (err.response.data.message !== '') {
                    toast.error(`Failed! ${err.response.data.message}`);
                } else {
                    toast.error('Failed to change the password.');
                }
            });
    };

    return (
        <Dialog open={true} onClose={onClose}>
            <DialogTitle sx={{ fontSize: '16px' }}>Change Password</DialogTitle>
            <DialogContent sx={{ minWidth: '300px' }}>
                <Box sx={{ mb: 3, mt: 3 }}>
                    <TextField
                        sx={{ width: '100%' }}
                        label="Old Password"
                        type="password"
                        value={oldPassword}
                        onChange={(e) => setOldPassword(e.target.value)}
                    />
                </Box>
                <Box sx={{ mb: 3, mt: 3 }}>
                    <TextField
                        sx={{ width: '100%' }}
                        label="New Password"
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                    />
                </Box>
                <Box sx={{ mb: 3, mt: 3 }}>
                    <TextField
                        sx={{ width: '100%' }}
                        label="Confirm Password"
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                </Box>
                <Box sx={{ mt: 3, mb: 3 }}>
                    <ReCAPTCHA sitekey={config.recaptchaSiteKey} onChange={() => setIsCaptchaSuccess(true)} />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSubmit} disabled={!isCaptchaSuccess}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

ChangePasswordDialog.propTypes = {
    onClose: PropTypes.func
};

export default ChangePasswordDialog;
